import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Key, Globe, Save, Loader2, CheckCircle2, Send, Link2, Plus, Trash2, Copy, UserPlus, ShieldCheck, Building2, Bot, Upload, Image, Shield, AlertTriangle, Phone, Download, FileSpreadsheet, ArrowRight, Palette, RefreshCw, Trash, Database } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import PageHeader from "../components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { BrandingConfigurator } from "../components/BrandingConfigurator";
import { forceClearCacheAndReload } from "../components/VersionUpdateBanner";
import HelpIcon from "../components/help/HelpIcon";
import { SecurityAlertsView } from "./SecurityAlertsPage";
import TimeTrackingCard from "../components/integrations/TimeTrackingCard";
import MetaTemplatesManager from "../components/integrations/MetaTemplatesManager";
import MetaQuotasCard from "../components/integrations/MetaQuotasCard";
import SecuritySettingsPanel from "../components/security/SecuritySettingsPanel";
import LoginProtectionCard from "../components/security/LoginProtectionCard";
import TwoFactorCard from "../components/security/TwoFactorCard";

const inputCls = "bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935] h-10";

export default function SettingsPage() {
  const { axiosAuth, user } = useAuth();
  const api = axiosAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isSuperadmin = user?.role === "superadmin"; // Master Blitzz cross-tenant access
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  // Read ?tab=xxx from URL (defaults to "inscriptions"). Updated when URL changes.
  const VALID_TABS = ["inscriptions", "api", "general", "entreprise", "agents", "securite", "import_export"];
  const queryTab = new URLSearchParams(location.search).get("tab");
  const activeTab = VALID_TABS.includes(queryTab) ? queryTab : "inscriptions";

  const handleTabChange = (value) => {
    const params = new URLSearchParams(location.search);
    params.set("tab", value);
    // Drop the section anchor when switching tabs manually
    params.delete("section");
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
  };

  // Scroll to section anchor (?section=xxx) once the tab is rendered.
  useEffect(() => {
    const section = new URLSearchParams(location.search).get("section");
    if (!section) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-section="${section}"], #section-${section}, [data-testid*="${section}"]`);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [location.search]);

  const load = async () => {
    if (!isPlatformAdmin) { setLoading(false); return; }
    try {
      const r = await api.get("/admin/settings");
      setSettings(r.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.put("/admin/settings", settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      load();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const testEmail = async () => {
    setTestingEmail(true);
    try {
      await api.post("/admin/settings/test-email", {});
      alert("Email de test envoye !");
    } catch (e) {
      alert("Erreur: " + (e.response?.data?.detail || e.message));
    }
    setTestingEmail(false);
  };

  const set = (key, val) => setSettings({ ...settings, [key]: val });

  // Invite links
  const [inviteLinks, setInviteLinks] = useState([]);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "" });
  const [copied, setCopied] = useState("");

  const loadInvites = async () => {
    try {
      const r = await api.get("/admin/invite-links");
      setInviteLinks(r.data);
    } catch {}
  };

  const createInvite = async () => {
    try {
      await api.post("/admin/invite-links", inviteForm);
      setShowInviteDialog(false);
      setInviteForm({ email: "", name: "" });
      loadInvites();
    } catch (e) { console.error(e); }
  };

  const deleteInvite = async (token) => {
    await api.delete(`/admin/invite-links/${token}`);
    loadInvites();
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/login?invite=${token}`;
    navigator.clipboard.writeText(url);
    setCopied(token);
    setTimeout(() => setCopied(""), 2000);
  };

  // Agents IA
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);

  // Logo
  const [companyLogo, setCompanyLogo] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const loadLogo = async () => {
    try { const r = await api.get("/admin/settings/logo"); setCompanyLogo(r.data?.company_logo || ""); } catch {}
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert("Max 2MB"); return; }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64 = ev.target.result;
      setUploadingLogo(true);
      try {
        await api.put("/admin/settings/logo", { company_logo: base64 });
        setCompanyLogo(base64);
      } catch (err) { alert("Erreur upload"); }
      setUploadingLogo(false);
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = async () => {
    setUploadingLogo(true);
    try { await api.put("/admin/settings/logo", { company_logo: "" }); setCompanyLogo(""); } catch {}
    setUploadingLogo(false);
  };

  const loadAgents = async () => {
    setAgentsLoading(true);
    try {
      const r = await api.get("/ai/agents");
      setAgents(r.data || []);
    } catch {}
    setAgentsLoading(false);
  };

  const saveAgent = async (agentType, updates) => {
    try {
      await api.put(`/ai/agents/${agentType}`, updates);
      loadAgents();
      setEditingAgent(null);
    } catch (e) { alert("Erreur: " + (e.response?.data?.detail || e.message)); }
  };

  const isPlatformAdmin = ["admin", "superadmin"].includes(user?.role);
  useEffect(() => {
    if (isPlatformAdmin) loadInvites();
    loadAgents();
    loadLogo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlatformAdmin]);

  if (loading) return <div className="p-8 text-slate-400">Chargement...</div>;

  return (
    <div className="p-8 max-w-4xl" data-testid="settings-page">
      <PageHeader
        icon={<Globe />}
        title="Paramètres"
        description="Configuration de la plateforme"
        testId="settings-header"
        actions={(
          <Button onClick={save} disabled={saving} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="settings-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : saved ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {saved ? "Enregistré !" : "Enregistrer"}
          </Button>
        )}
      />

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="bg-[#14101c] border border-white/10 mb-6">
          <TabsTrigger value="inscriptions" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c] gap-2" data-testid="settings-tab-inscriptions">
            <ShieldCheck className="w-4 h-4" /> Inscriptions
          </TabsTrigger>
          <TabsTrigger value="api" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c] gap-2" data-testid="settings-tab-api">
            <Key className="w-4 h-4" /> Clés API
          </TabsTrigger>
          <TabsTrigger value="general" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c] gap-2" data-testid="settings-tab-general">
            <Globe className="w-4 h-4" /> General
          </TabsTrigger>
          <TabsTrigger value="entreprise" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c] gap-2" data-testid="settings-tab-entreprise">
            <Building2 className="w-4 h-4" /> Entreprise
          </TabsTrigger>
          <TabsTrigger value="agents" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c] gap-2" data-testid="settings-tab-agents">
            <Bot className="w-4 h-4" /> Agents IA
          </TabsTrigger>
          <TabsTrigger value="securite" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c] gap-2" data-testid="settings-tab-securite">
            <Shield className="w-4 h-4" /> Sécurité
          </TabsTrigger>
          <TabsTrigger value="import_export" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c] gap-2" data-testid="settings-tab-import">
            <Upload className="w-4 h-4" /> Import / Export
          </TabsTrigger>
        </TabsList>

        {/* Inscriptions Tab */}
        <TabsContent value="inscriptions">
          <div className="space-y-6">
            {/* Toggle registration */}
            <div className="bg-[#14101c] border border-white/10 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: settings.registration_enabled ? "#22c55e20" : "#ef444420", color: settings.registration_enabled ? "#22c55e" : "#ef4444" }}>
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold">Création de compte public</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Autorise les visiteurs à créer un compte depuis la page de connexion</p>
                  </div>
                </div>
                <button
                  onClick={() => set("registration_enabled", !settings.registration_enabled)}
                  data-testid="toggle-registration"
                  className={`relative w-14 h-7 rounded-full transition-colors ${settings.registration_enabled ? "bg-green-500" : "bg-slate-600"}`}
                >
                  <div className={`absolute top-0.5 w-6 h-6 rounded-full bg-white transition-transform ${settings.registration_enabled ? "translate-x-7" : "translate-x-0.5"}`} />
                </button>
              </div>
              <p className="text-sm text-slate-400 mt-4 pl-14">
                {settings.registration_enabled
                  ? "Les visiteurs peuvent creer un compte librement."
                  : "Les comptes ne peuvent être créés que par l'admin ou via un lien d'invitation."}
              </p>
            </div>

            {/* Invite links */}
            <div className="bg-[#14101c] border border-white/10 rounded-xl p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-500/10 text-blue-400">
                    <Link2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold">Liens d'invitation</h3>
                    <p className="text-xs text-slate-500">Créez des liens uniques pour inviter des clients</p>
                  </div>
                </div>
                <Button onClick={() => setShowInviteDialog(true)} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="create-invite-btn">
                  <Plus className="w-4 h-4 mr-2" /> Créer un lien
                </Button>
              </div>

              {inviteLinks.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-sm">Aucun lien d'invitation. Cliquez sur "Créer un lien" pour en générer un.</div>
              ) : (
                <div className="space-y-2">
                  {inviteLinks.map((inv) => (
                    <div key={inv.invite_token} className="flex items-center gap-3 p-3 rounded-lg border border-white/5 bg-[#0b0811] group" data-testid={`invite-${inv.invite_token}`}>
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${inv.status === "pending" ? "bg-green-400" : "bg-slate-500"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{inv.email || inv.name || "Sans email"}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${inv.status === "pending" ? "bg-green-500/10 text-green-400" : "bg-slate-500/10 text-slate-400"}`}>
                            {inv.status === "pending" ? "Actif" : "Utilise"}
                          </span>
                        </div>
                        <div className="text-xs text-slate-600 truncate mt-0.5">
                          {window.location.origin}/login?invite={inv.invite_token}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <button onClick={() => copyLink(inv.invite_token)} className="p-1.5 rounded hover:bg-white/10 text-slate-400 hover:text-white" data-testid={`copy-invite-${inv.invite_token}`}>
                          {copied === inv.invite_token ? <CheckCircle2 className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                        </button>
                        <button onClick={() => deleteInvite(inv.invite_token)} className="p-1.5 rounded hover:bg-red-500/10" data-testid={`delete-invite-${inv.invite_token}`}>
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* API Keys Tab — Iter 266 : clés centralisées dans le Hub API */}
        <TabsContent value="api">
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-[#f7c935]/10 to-transparent border border-[#f7c935]/25 rounded-xl p-6" data-testid="api-hub-redirect-card">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-[#f7c935]/15 text-[#f7c935] flex-shrink-0">
                  <Key className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-white">Les clés API sont désormais gérées dans le Hub API</h3>
                  <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                    Configuration et surveillance de toutes les intégrations (Cegid, GoCardless, Resend, Gmail, Aircall, Meta, Yousign, Shopify, Stripe, Tavily/Pappers, Storecove, Google, OneDrive…) centralisées sur une seule page.
                  </p>
                  <Button onClick={() => navigate("/admin/integrations-health")} className="mt-3 bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="go-to-api-hub-btn">
                    <ArrowRight className="w-4 h-4 mr-2" /> Ouvrir le Hub API
                  </Button>
                </div>
              </div>
            </div>

            <ApiSectionHeader title="Outils opérationnels" subtitle="Réglages métier (sans clés API) : pointage, templates Meta" color="#3b82f6" />
            <div data-section="time_tracking" id="section-time_tracking">
              <TimeTrackingCard />
            </div>
            <MetaTemplatesManager />
            <MetaQuotasCard />
          </div>
        </TabsContent>

        {/* General Tab */}
        <TabsContent value="general">
          <div className="space-y-6">
            <div className="bg-[#14101c] border border-white/10 rounded-xl p-6 space-y-5">
              <h3 className="text-lg font-bold" style={{ color: "#f7c935" }}>Preferences</h3>
              <div>
                <label className="text-xs text-slate-500 mb-1.5 block">Langue</label>
                <select value={settings.language || "fr"} onChange={(e) => set("language", e.target.value)}
                  className="w-full max-w-xs bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm" data-testid="setting-language">
                  <option value="fr">Francais</option>
                  <option value="en">English</option>
                </select>
              </div>
            </div>
            <div className="bg-[#14101c] border border-white/10 rounded-xl p-6 space-y-4" data-testid="sender-email-section">
              <h3 className="text-lg font-bold" style={{ color: "#f7c935" }}>Expéditeur email</h3>
              <div className="grid md:grid-cols-2 gap-4 max-w-2xl">
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">Email expediteur</label>
                  <Input value={settings.sender_email || ""} onChange={(e) => set("sender_email", e.target.value)} placeholder="contact@blitzz.fr" className={inputCls} data-testid="gmail-sender-email" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">Nom expediteur</label>
                  <Input value={settings.sender_name || ""} onChange={(e) => set("sender_name", e.target.value)} placeholder="Blitzz" className={inputCls} data-testid="gmail-sender-name" />
                </div>
              </div>
              <p className="text-[11px] text-slate-500">Les clés d'envoi (Gmail, Resend) se configurent dans le Hub API.</p>
              <Button onClick={testEmail} disabled={testingEmail} variant="outline" className="border-white/10 text-white hover:bg-white/5" data-testid="test-email-btn">
                {testingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Tester l'envoi
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Entreprise Tab */}
        <TabsContent value="entreprise">
          <div className="space-y-6">
            {/* Blitzz Master branding — visible only for superadmin */}
            {isSuperadmin && (
              <div className="bg-gradient-to-br from-[#f7c935]/10 to-[#f7c935]/5 border border-[#f7c935]/20 rounded-xl p-5" data-testid="blitzz-branding-section">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#f7c935]/20 text-[#f7c935]">
                    <Palette className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold flex items-center gap-2">
                      Marque blanche Blitzz
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#f7c935] text-[#0b0811] font-bold uppercase tracking-wider">Master</span>
                    </h3>
                    <p className="text-xs text-slate-500">Logo, couleurs, ecran de connexion de la plateforme Blitzz elle-meme.</p>
                  </div>
                </div>
                <BrandingConfigurator compact blitzzMaster />
              </div>
            )}

            {/* Logo upload */}
            <div className="bg-[#14101c] border border-white/10 rounded-xl p-6" data-testid="logo-section">
              <h3 className="text-lg font-bold mb-2" style={{ color: "#f7c935" }}>Logo de l'entreprise</h3>
              <p className="text-xs text-slate-500 mb-4">Ce logo apparaitra sur vos devis, factures et dans la sidebar. Format: PNG, JPG, SVG (max 2MB).</p>
              <div className="flex items-center gap-6">
                <div className="w-32 h-32 rounded-xl border-2 border-dashed border-white/10 flex items-center justify-center bg-[#0b0811] overflow-hidden flex-shrink-0">
                  {companyLogo ? (
                    <img src={companyLogo} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="logo-preview" />
                  ) : (
                    <Image className="w-10 h-10 text-slate-600" />
                  )}
                </div>
                <div className="space-y-3">
                  <label className="cursor-pointer">
                    <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={handleLogoUpload} className="hidden" data-testid="logo-upload-input" />
                    <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#f7c935] text-[#14101c] font-bold text-sm hover:bg-[#e6b825] transition-colors cursor-pointer">
                      {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {uploadingLogo ? "Chargement..." : companyLogo ? "Changer le logo" : "Uploader un logo"}
                    </div>
                  </label>
                  {companyLogo && (
                    <button onClick={removeLogo} className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300 transition-colors" data-testid="remove-logo-btn">
                      <Trash2 className="w-3 h-3" /> Supprimer le logo
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-[#14101c] border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-4" style={{ color: "#f7c935" }}>Informations legales</h3>
              <p className="text-xs text-slate-500 mb-5">Ces informations apparaitront sur vos factures et devis (conformite loi de facturation electronique FR).</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Nom de l'entreprise</label>
                  <Input value={settings.admin_company || ""} onChange={e => set("admin_company", e.target.value)} className={inputCls} data-testid="setting-admin_company" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Forme juridique</label>
                  <Input value={settings.admin_forme_juridique || ""} onChange={e => set("admin_forme_juridique", e.target.value)} placeholder="SAS, SARL, EURL..." className={inputCls} data-testid="setting-forme_juridique" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">SIREN</label>
                  <Input value={settings.admin_siren || ""} onChange={e => set("admin_siren", e.target.value)} placeholder="123 456 789" className={inputCls} data-testid="setting-admin_siren" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">SIRET</label>
                  <Input value={settings.admin_siret || ""} onChange={e => set("admin_siret", e.target.value)} placeholder="123 456 789 00001" className={inputCls} data-testid="setting-admin_siret" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">N TVA Intracommunautaire</label>
                  <Input value={settings.admin_tva_intra || ""} onChange={e => set("admin_tva_intra", e.target.value)} placeholder="FR12345678901" className={inputCls} data-testid="setting-admin_tva_intra" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Capital social</label>
                  <Input value={settings.admin_capital || ""} onChange={e => set("admin_capital", e.target.value)} placeholder="10 000 EUR" className={inputCls} data-testid="setting-admin_capital" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">RCS</label>
                  <Input value={settings.admin_rcs || ""} onChange={e => set("admin_rcs", e.target.value)} placeholder="RCS Paris B 123 456 789" className={inputCls} data-testid="setting-admin_rcs" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Telephone</label>
                  <Input value={settings.admin_phone || ""} onChange={e => set("admin_phone", e.target.value)} placeholder="+33 1 23 45 67 89" className={inputCls} />
                </div>
              </div>
            </div>

            <div className="bg-[#14101c] border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-4" style={{ color: "#f7c935" }}>Adresse</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-xs text-slate-500 mb-1 block">Adresse</label>
                  <Input value={settings.admin_address || ""} onChange={e => set("admin_address", e.target.value)} placeholder="123 rue de la Paix" className={inputCls} data-testid="setting-admin_address" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Code postal</label>
                  <Input value={settings.admin_zip || ""} onChange={e => set("admin_zip", e.target.value)} placeholder="75001" className={inputCls} />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Ville</label>
                  <Input value={settings.admin_city || ""} onChange={e => set("admin_city", e.target.value)} placeholder="Paris" className={inputCls} />
                </div>
              </div>
            </div>

            <div className="bg-[#14101c] border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-4" style={{ color: "#f7c935" }}>Coordonnees bancaires</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-xs text-slate-500 mb-1 block">IBAN</label>
                  <Input value={settings.admin_iban || ""} onChange={e => set("admin_iban", e.target.value)} placeholder="FR76 1234 5678 9012 3456 7890 123" className={inputCls} data-testid="setting-admin_iban" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">BIC</label>
                  <Input value={settings.admin_bic || ""} onChange={e => set("admin_bic", e.target.value)} placeholder="BNPAFRPP" className={inputCls} />
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Agents IA Tab */}
        <TabsContent value="agents">
          <div className="space-y-4" data-testid="settings-agents-tab">
            <p className="text-sm text-slate-400 mb-4">Configurez les agents IA de la plateforme. Activez ou desactivez les fonctions de chaque agent.</p>
            {agentsLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>
            ) : agents.map(agent => (
              <div key={agent.agent_id} className="bg-[#14101c] border border-white/10 rounded-xl p-5" data-testid={`agent-config-${agent.type}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${agent.color}20` }}>
                    <Bot className="w-5 h-5" style={{ color: agent.color }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold">{agent.name}</h3>
                    <p className="text-xs text-slate-500">{agent.description}</p>
                  </div>
                  <button onClick={() => saveAgent(agent.type, { enabled: !agent.enabled })}
                    className={`relative w-14 h-7 rounded-full transition-colors ${agent.enabled ? "bg-green-500" : "bg-slate-600"}`}
                    data-testid={`toggle-agent-${agent.type}`}>
                    <div className={`absolute top-0.5 w-6 h-6 rounded-full bg-white transition-transform ${agent.enabled ? "translate-x-7" : "translate-x-0.5"}`} />
                  </button>
                </div>
                {/* Functions */}
                <div className="space-y-2 ml-13">
                  <div className="text-[10px] text-slate-500 uppercase font-bold mb-2">Fonctions</div>
                  {(agent.functions || []).map(fn => (
                    <div key={fn.id} className="flex items-center gap-3 py-1.5">
                      <button
                        onClick={() => {
                          const updated = (agent.functions || []).map(f => f.id === fn.id ? { ...f, enabled: !f.enabled } : f);
                          saveAgent(agent.type, { functions: updated });
                        }}
                        className={`w-5 h-5 rounded flex items-center justify-center border transition-colors ${fn.enabled ? "border-green-500 bg-green-500/20" : "border-white/20 bg-transparent"}`}
                        data-testid={`toggle-fn-${agent.type}-${fn.id}`}>
                        {fn.enabled && <CheckCircle2 className="w-3 h-3 text-green-400" />}
                      </button>
                      <span className="text-sm text-slate-300">{fn.name}</span>
                    </div>
                  ))}
                </div>
                {/* Edit system prompt */}
                <div className="mt-4">
                  <button onClick={() => setEditingAgent(editingAgent === agent.type ? null : agent.type)}
                    className="text-xs text-[#f7c935] hover:underline" data-testid={`edit-prompt-${agent.type}`}>
                    {editingAgent === agent.type ? "Fermer" : "Modifier le prompt systeme"}
                  </button>
                  {editingAgent === agent.type && (
                    <div className="mt-3">
                      <textarea
                        value={agent.system_prompt || ""}
                        onChange={e => setAgents(agents.map(a => a.type === agent.type ? { ...a, system_prompt: e.target.value } : a))}
                        className="w-full bg-[#0b0811] border border-white/10 text-sm text-white rounded-lg px-3 py-2 h-40 resize-y"
                        data-testid={`prompt-textarea-${agent.type}`} />
                      <Button onClick={() => saveAgent(agent.type, { system_prompt: agent.system_prompt })}
                        className="mt-2 bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] text-xs font-bold" data-testid={`save-prompt-${agent.type}`}>
                        <Save className="w-3 h-3 mr-1" /> Sauvegarder le prompt
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="securite">
          <SecurityPanel api={api} inputCls={inputCls} />
          <div className="bg-[#14101c] border border-white/10 rounded-xl p-6 mt-6" data-testid="security-settings-section">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-4 h-4 text-amber-400" />
              <h3 className="text-sm font-bold text-white">Réglages anti-bot & blocage IP</h3>
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 uppercase font-bold">Iter 216</span>
            </div>
            <p className="text-[11px] text-slate-500 mb-3">Seuils de blocage automatique, allowlist/blocklist d'IPs, durée des blocages.</p>
            <LoginProtectionCard />
            <SecuritySettingsPanel />
          </div>
          <TwoFactorCard />
          <div className="bg-[#14101c] border border-white/10 rounded-xl p-6 mt-6" data-testid="security-alerts-embedded">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-4 h-4 text-rose-400" />
              <h3 className="text-sm font-bold text-white">Alertes & carte mondiale</h3>
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 uppercase font-bold">DevTools · Bots · Géo-IP</span>
            </div>
            <p className="text-[11px] text-slate-500 mb-3">Activité suspecte détectée côté client (DevTools, scrapping, bots) + backend (brute-force, burst API). Carte mondiale des origines.</p>
            <SecurityAlertsView embedded />
          </div>
        </TabsContent>

        {/* Import / Export Tab */}
        <TabsContent value="import_export">
          <ImportExportPanel api={api} />
        </TabsContent>
      </Tabs>

      {/* Invite creation dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="text-xl font-bold">Créer un lien d'invitation</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-xs text-slate-500">Le lien permettra a une personne de creer son compte client sur la plateforme.</p>
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Email (optionnel)</label>
              <Input value={inviteForm.email} onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })} placeholder="email@client.fr" className={inputCls} data-testid="invite-email-input" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Nom (optionnel)</label>
              <Input value={inviteForm.name} onChange={e => setInviteForm({ ...inviteForm, name: e.target.value })} placeholder="Nom du client" className={inputCls} data-testid="invite-name-input" />
            </div>
            <Button onClick={createInvite} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="confirm-create-invite-btn">
              <Link2 className="w-4 h-4 mr-2" /> Generer le lien
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function SecurityPanel({ api, inputCls }) {
  const [dashboard, setDashboard] = useState(null);
  const [events, setEvents] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [twilio, setTwilio] = useState({ twilio_account_sid: "", twilio_auth_token: "", twilio_phone_number: "" });
  const [twilioConfigured, setTwilioConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testingSms, setTestingSms] = useState(false);
  const [features, setFeatures] = useState([]);
  const [savingFeatures, setSavingFeatures] = useState(false);

  useEffect(() => {
    loadSecurity();
  }, []);

  const loadSecurity = async () => {
    setLoading(true);
    try {
      const [d, e, c, f] = await Promise.all([
        api.get("/security/dashboard").catch(() => ({ data: null })),
        api.get("/security/events?limit=20").catch(() => ({ data: [] })),
        api.get("/security/alert-config").catch(() => ({ data: {} })),
        api.get("/security/features").catch(() => ({ data: [] })),
      ]);
      setDashboard(d.data);
      setEvents(e.data || []);
      setFeatures(f.data || []);
      const cfg = c.data || {};
      setContacts(cfg.contacts?.length ? cfg.contacts : [{ name: "", email: "", phone: "" }]);
      setTwilio({
        twilio_account_sid: cfg.twilio_account_sid || "",
        twilio_auth_token: cfg.twilio_auth_token || "",
        twilio_phone_number: cfg.twilio_phone_number || "",
      });
      setTwilioConfigured(cfg.twilio_configured || false);
    } catch {}
    setLoading(false);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put("/security/alert-config", { contacts, ...twilio });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      loadSecurity();
    } catch {}
    setSaving(false);
  };

  const addContact = () => setContacts([...contacts, { name: "", email: "", phone: "" }]);
  const removeContact = (i) => setContacts(contacts.filter((_, idx) => idx !== i));
  const updateContact = (i, field, val) => {
    const c = [...contacts];
    c[i] = { ...c[i], [field]: val };
    setContacts(c);
  };

  const testSms = async (phone) => {
    if (!phone) return;
    setTestingSms(true);
    try {
      await api.post("/security/test-sms", { phone });
      alert("SMS de test envoye !");
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    }
    setTestingSms(false);
  };

  const unblockIp = async (ip) => {
    try {
      await api.delete(`/security/unblock/${ip}`);
      loadSecurity();
    } catch {}
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>;

  const severityColor = { critical: "text-red-400 bg-red-500/10", warning: "text-amber-400 bg-amber-500/10", info: "text-blue-400 bg-blue-500/10" };

  return (
    <div className="space-y-6" data-testid="security-panel">

      {/* ─── Security Feature Toggles ─── */}
      {features.length > 0 && (
        <div className="bg-[#14101c] border border-white/10 rounded-xl p-6" data-testid="security-features">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#f7c935]/10 text-[#f7c935]">
                <Shield className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-white">Parametres de securite</h3>
                <p className="text-xs text-slate-500">Activez ou desactivez les fonctionnalites de protection</p>
              </div>
            </div>
            <Button onClick={async () => {
              setSavingFeatures(true);
              try {
                const toggles = {};
                features.forEach(f => { toggles[f.key] = f.enabled; });
                await api.put("/security/features", { features: toggles });
                setSaved(true);
                setTimeout(() => setSaved(false), 2000);
              } catch {}
              setSavingFeatures(false);
            }} disabled={savingFeatures} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold text-xs" data-testid="save-security-features">
              {savingFeatures ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Sauvegarder
            </Button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {features.map(f => (
              <div key={f.key} className={`flex items-start gap-3 p-3.5 rounded-xl border transition-colors ${f.enabled ? "bg-[#0b0811] border-[#f7c935]/20" : "bg-[#0b0811] border-white/5"}`} data-testid={`feature-${f.key}`}>
                <button onClick={() => setFeatures(prev => prev.map(p => p.key === f.key ? { ...p, enabled: !p.enabled } : p))}
                  className={`mt-0.5 w-10 h-5 rounded-full relative flex-shrink-0 transition-colors ${f.enabled ? "bg-[#f7c935]" : "bg-white/10"}`}
                  data-testid={`toggle-${f.key}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${f.enabled ? "left-[22px]" : "left-0.5"}`} />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-white">{f.label}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">{f.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Cache Management ─── */}
      <CacheManagementCard />

      {/* ─── Multi-Contact Alerts ─── */}
      <div className="bg-[#14101c] border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-red-500/10 text-red-400">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-white">Contacts d'alerte</h3>
              <p className="text-xs text-slate-500">En cas d'attaque, ces personnes recevront un email + SMS</p>
            </div>
          </div>
          <Button onClick={addContact} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs" data-testid="add-alert-contact">
            <Plus className="w-3 h-3 mr-1" /> Ajouter
          </Button>
        </div>

        <div className="space-y-3">
          {contacts.map((c, i) => (
            <div key={i} className="flex items-start gap-2 p-3 bg-[#0b0811] border border-white/[0.06] rounded-lg" data-testid={`alert-contact-${i}`}>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-bold mb-1 block">Nom</label>
                  <Input value={c.name} onChange={e => updateContact(i, "name", e.target.value)}
                    placeholder="Jean Dupont" className={inputCls} data-testid={`contact-name-${i}`} />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-bold mb-1 block">Email</label>
                  <Input value={c.email} onChange={e => updateContact(i, "email", e.target.value)}
                    placeholder="jean@blitzz.fr" className={inputCls} data-testid={`contact-email-${i}`} />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-bold mb-1 block">Telephone</label>
                  <div className="flex gap-1">
                    <Input value={c.phone} onChange={e => updateContact(i, "phone", e.target.value)}
                      placeholder="+33 6 12 34 56 78" className={`${inputCls} flex-1`} data-testid={`contact-phone-${i}`} />
                    {twilioConfigured && c.phone && (
                      <button onClick={() => testSms(c.phone)} disabled={testingSms}
                        className="px-2 py-1 text-[10px] rounded bg-green-500/10 text-green-400 hover:bg-green-500/20 whitespace-nowrap"
                        data-testid={`test-sms-${i}`}>
                        {testingSms ? "..." : "Test SMS"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {contacts.length > 1 && (
                <button onClick={() => removeContact(i)} className="mt-6 p-1 rounded hover:bg-red-500/10 text-slate-500 hover:text-red-400" data-testid={`remove-contact-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ─── Twilio SMS Configuration ─── */}
      <div className="bg-[#14101c] border border-white/10 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-purple-500/10 text-purple-400">
            <Phone className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-white">Twilio SMS</h3>
            <p className="text-xs text-slate-500">
              Envoi de SMS d'alerte en cas d'attaque — <a href="https://console.twilio.com" target="_blank" rel="noreferrer" className="text-[#f7c935] underline">console.twilio.com</a>
            </p>
          </div>
          {twilioConfigured && (
            <span className="ml-auto text-[10px] px-2 py-1 rounded-full bg-green-500/10 text-green-400 font-bold" data-testid="twilio-status-badge">
              Configure
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs text-slate-500 mb-1.5 block">Account SID</label>
            <Input value={twilio.twilio_account_sid} onChange={e => setTwilio({ ...twilio, twilio_account_sid: e.target.value })}
              placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" className={inputCls} data-testid="twilio-sid" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1.5 block">Auth Token</label>
            <Input type="password" value={twilio.twilio_auth_token} onChange={e => setTwilio({ ...twilio, twilio_auth_token: e.target.value })}
              placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" className={inputCls} data-testid="twilio-token" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1.5 block">Numéro expéditeur</label>
            <Input value={twilio.twilio_phone_number} onChange={e => setTwilio({ ...twilio, twilio_phone_number: e.target.value })}
              placeholder="+33 X XX XX XX XX" className={inputCls} data-testid="twilio-phone" />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={saveConfig} disabled={saving} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="save-alert-config">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : saved ? <CheckCircle2 className="w-4 h-4 mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          {saved ? "Sauvegarde !" : "Sauvegarder la configuration"}
        </Button>
      </div>

      {/* ─── Dashboard Stats ─── */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: "Evenements 24h", value: dashboard.events_24h, color: "#3b82f6" },
            { label: "Critiques 24h", value: dashboard.critical_24h, color: dashboard.critical_24h > 0 ? "#ef4444" : "#22c55e" },
            { label: "Evenements 7j", value: dashboard.events_7d, color: "#8b5cf6" },
            { label: "IPs bloquees 24h", value: dashboard.unique_blocked_24h, color: "#f59e0b" },
            { label: "Bloquees actuel", value: dashboard.currently_blocked, color: dashboard.currently_blocked > 0 ? "#ef4444" : "#22c55e" },
          ].map(s => (
            <div key={s.label} className="bg-[#14101c] border border-white/5 rounded-xl p-4">
              <div className="text-[10px] text-slate-500 uppercase tracking-wide font-bold mb-1">{s.label}</div>
              <div className="text-2xl font-black" style={{ color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* ─── Top IPs ─── */}
      {dashboard?.top_ips?.length > 0 && (
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-5">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4 text-red-400" /> IPs les plus suspectes (7 jours)
          </h3>
          <div className="space-y-2">
            {dashboard.top_ips.map((ip, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-[#0b0811] border border-white/[0.06] rounded-lg">
                <span className="text-sm font-mono text-[#f7c935] min-w-[130px]">{ip.ip}</span>
                <span className="text-xs text-slate-400">{ip.count} evenements</span>
                <div className="flex gap-1 flex-1">
                  {ip.types?.map(t => (
                    <span key={t} className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-400">{t}</span>
                  ))}
                </div>
                <button onClick={() => unblockIp(ip.ip)} className="text-[10px] px-2 py-1 rounded bg-green-500/10 text-green-400 hover:bg-green-500/20">
                  Debloquer
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Recent Events ─── */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-white/[0.06]">
          <h3 className="text-sm font-bold text-white">Journal de sécurité ({events.length} derniers)</h3>
        </div>
        {events.length === 0 ? (
          <div className="text-center py-8 text-xs text-slate-500">Aucun événement de sécurité. Bonne nouvelle !</div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/[0.06]">
                <th className="px-5 py-2.5 text-[10px] text-[#888194] uppercase font-bold">Date</th>
                <th className="px-4 py-2.5 text-[10px] text-[#888194] uppercase font-bold">Severite</th>
                <th className="px-4 py-2.5 text-[10px] text-[#888194] uppercase font-bold">Type</th>
                <th className="px-4 py-2.5 text-[10px] text-[#888194] uppercase font-bold">IP</th>
                <th className="px-4 py-2.5 text-[10px] text-[#888194] uppercase font-bold">Details</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev, i) => (
                <tr key={i} className="border-b border-white/[0.03] hover:bg-white/[0.02]" data-testid={`security-event-${i}`}>
                  <td className="px-5 py-2.5 text-[11px] text-slate-400">{new Date(ev.timestamp).toLocaleString("fr-FR")}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${severityColor[ev.severity] || "text-slate-400 bg-slate-500/10"}`}>
                      {ev.severity}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-white font-medium">{ev.type}</td>
                  <td className="px-4 py-2.5 text-xs font-mono text-[#f7c935]">{ev.ip}</td>
                  <td className="px-4 py-2.5 text-[11px] text-slate-400 max-w-[300px] truncate">{ev.details}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


function ImportExportPanel({ api }) {
  const [activeTab, setActiveTab] = useState("clients");
  const [fileRef] = useState({ current: null });
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const importTabs = [
    { key: "clients", label: "Clients", icon: "users", desc: "Importez votre base clients (CSV, XLSX, PDF)" },
    { key: "crm", label: "Echanges CRM", icon: "clipboard", desc: "Importez appels, emails, notes dans les fiches clients" },
    { key: "invoices", label: "Factures", icon: "receipt", desc: "Importez vos factures avec detection statut et doublons" },
  ];

  const endpoints = {
    clients: { analyze: "/import/clients/analyze", execute: "/import/clients/execute" },
    crm: { analyze: "/import/crm-exchanges/analyze", execute: "/import/crm-exchanges/execute" },
    invoices: { analyze: "/import/invoices/analyze", execute: "/import/invoices/execute" },
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAnalyzing(true);
    setError("");
    setResult(null);
    setAnalysis(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const r = await api.post(endpoints[activeTab].analyze, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalysis(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'analyse");
    }
    setAnalyzing(false);
  };

  const executeImport = async () => {
    if (!analysis) return;
    setExecuting(true);
    setError("");
    try {
      const r = await api.post(endpoints[activeTab].execute, analysis);
      setResult(r.data);
      setAnalysis(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'import");
    }
    setExecuting(false);
  };

  const handleExport = async (type) => {
    setExporting(true);
    try {
      const r = await api.get(`/export/${type}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `export_${type}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Erreur export: " + (err.response?.data?.detail || err.message));
    }
    setExporting(false);
  };

  return (
    <div className="space-y-6" data-testid="import-export-panel">
      {/* Import Section */}
      <div className="bg-[#14101c] border border-white/10 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#f7c935]/10 text-[#f7c935]">
            <Upload className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-white">Import IA</h3>
            <p className="text-xs text-slate-500">Importez des donnees avec analyse et mapping automatique par IA</p>
          </div>
        </div>

        <div className="flex gap-2 mb-5">
          {importTabs.map(t => (
            <button key={t.key} onClick={() => { setActiveTab(t.key); setAnalysis(null); setResult(null); setError(""); }}
              className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors ${activeTab === t.key ? "bg-[#f7c935] text-[#14101c]" : "bg-white/5 text-slate-400 hover:text-white"}`}
              data-testid={`import-tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>

        <p className="text-xs text-slate-500 mb-4">{importTabs.find(t => t.key === activeTab)?.desc}</p>

        {/* Upload area */}
        {!analysis && !result && (
          <label className="block cursor-pointer">
            <input type="file" accept=".csv,.xlsx,.xls,.pdf" onChange={handleUpload} className="hidden" data-testid="import-file-input" />
            <div className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center hover:border-[#f7c935]/30 transition-colors">
              {analyzing ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 animate-spin text-[#f7c935]" />
                  <span className="text-sm text-slate-400">Analyse IA en cours...</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <FileSpreadsheet className="w-8 h-8 text-slate-500" />
                  <span className="text-sm text-slate-400">Cliquez pour selectionner un fichier</span>
                  <span className="text-[10px] text-slate-600">CSV, XLSX, XLS ou PDF</span>
                </div>
              )}
            </div>
          </label>
        )}

        {/* Analysis preview */}
        {analysis && !result && (
          <div className="space-y-4">
            <div className="bg-[#0b0811] border border-white/5 rounded-lg p-4">
              <div className="text-sm font-bold text-white mb-2">Apercu ({analysis.preview?.length || analysis.rows_preview?.length || 0} lignes)</div>
              <div className="text-xs text-slate-400">
                {analysis.total_rows || analysis.count || "?"} enregistrements detectes.
                {analysis.duplicates_count ? ` ${analysis.duplicates_count} doublons potentiels.` : ""}
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={executeImport} disabled={executing} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="execute-import-btn">
                {executing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                Lancer l'import
              </Button>
              <Button onClick={() => { setAnalysis(null); setError(""); }} variant="outline" className="border-white/10 text-white hover:bg-white/5">
                Annuler
              </Button>
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
            <div className="flex items-center gap-2 text-green-400 font-bold text-sm mb-1">
              <CheckCircle2 className="w-4 h-4" /> Import termine
            </div>
            <p className="text-xs text-slate-400">
              {result.created || result.count || 0} enregistrements importes.
              {result.skipped ? ` ${result.skipped} ignores.` : ""}
            </p>
            <Button onClick={() => { setResult(null); setAnalysis(null); }} variant="outline" className="mt-3 border-white/10 text-white hover:bg-white/5 text-xs">
              Nouvel import
            </Button>
          </div>
        )}

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-xs mt-3" data-testid="import-error">
            {error}
          </div>
        )}
      </div>

      {/* Export Section */}
      <div className="bg-[#14101c] border border-white/10 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-500/10 text-blue-400">
            <Download className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-white">Export</h3>
            <p className="text-xs text-slate-500">Exportez vos donnees au format CSV</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { key: "clients", label: "Clients", desc: "Base clients complete" },
            { key: "invoices", label: "Factures", desc: "Toutes les factures" },
            { key: "depenses", label: "Depenses", desc: "Journal des depenses" },
          ].map(exp => (
            <button key={exp.key} onClick={() => handleExport(exp.key)} disabled={exporting}
              className="p-4 rounded-xl border border-white/5 bg-[#0b0811] hover:border-[#f7c935]/20 transition-colors text-left group"
              data-testid={`export-${exp.key}-btn`}>
              <div className="text-sm font-bold text-white group-hover:text-[#f7c935] transition-colors">{exp.label}</div>
              <div className="text-[10px] text-slate-500 mt-1">{exp.desc}</div>
              <div className="flex items-center gap-1 mt-3 text-[10px] text-[#f7c935]">
                {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                Telecharger CSV
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}



/* ═══════════════════════════════════════════════════════════════
   CACHE MANAGEMENT CARD
   Pour Securite > Cache et Service Worker
   - Toggle: detection auto des nouvelles versions (banner)
   - Action: effacement ponctuel du cache + reload
   ═══════════════════════════════════════════════════════════════ */
function CacheManagementCard() {
  const [autoEnabled, setAutoEnabled] = useState(() => {
    return localStorage.getItem("blitzz_autocache_enabled") !== "false"; // default: enabled
  });
  const [clearing, setClearing] = useState(false);
  const [clearedAt, setClearedAt] = useState(null);

  const toggleAuto = () => {
    const next = !autoEnabled;
    setAutoEnabled(next);
    localStorage.setItem("blitzz_autocache_enabled", String(next));
  };

  const handleClear = async () => {
    if (!window.confirm("Effacer tous les caches et recharger la page ? Vos modifications non enregistrees seront perdues.")) return;
    setClearing(true);
    setClearedAt(new Date().toLocaleTimeString());
    setTimeout(() => forceClearCacheAndReload(), 500);
  };

  return (
    <div className="bg-[#14101c] border border-white/5 rounded-xl p-6 mb-6" data-testid="cache-management-card">
      <h3 className="text-sm font-bold text-white mb-1 flex items-center gap-2">
        <RefreshCw className="w-4 h-4 text-[#f7c935]" /> Cache & Service Worker
        <HelpIcon topic="cache_updates" />
      </h3>
      <p className="text-[11px] text-slate-500 mb-4 leading-relaxed">
        Gère la détection automatique des nouvelles versions de la plateforme et l'effacement manuel du cache local.
      </p>

      <div className="flex items-start gap-3 p-3.5 rounded-xl bg-[#0b0811] border border-white/5 mb-3" data-testid="cache-auto-toggle-wrapper">
        <button
          onClick={toggleAuto}
          className={`mt-0.5 w-10 h-5 rounded-full relative flex-shrink-0 transition-colors ${autoEnabled ? "bg-[#f7c935]" : "bg-white/10"}`}
          data-testid="cache-auto-toggle"
          aria-label="Toggle auto cache invalidation"
        >
          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${autoEnabled ? "left-[22px]" : "left-0.5"}`} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-white">Détection automatique des nouvelles versions</div>
          <div className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
            Le système vérifie chaque minute si une nouvelle version est disponible (après un déploiement) et propose un rechargement via un bandeau doré en bas de page.
            Recommandé pour rester à jour sans intervention manuelle.
          </div>
        </div>
      </div>

      <div className="p-3.5 rounded-xl bg-[#0b0811] border border-white/5" data-testid="cache-clear-section">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 w-9 h-9 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center flex-shrink-0">
            <Trash className="w-4 h-4 text-red-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-white">Effacement ponctuel du cache</div>
            <div className="text-[11px] text-slate-500 mt-0.5 leading-relaxed mb-3">
              Vide tous les caches (Service Worker, localStorage, IndexedDB) et recharge la page.
              Utile si vous voyez une ancienne version de l'interface ou en cas de bugs visuels persistants.
            </div>
            <button
              onClick={handleClear}
              disabled={clearing}
              className="text-xs px-3 py-1.5 rounded-lg bg-red-500/10 text-red-300 font-bold border border-red-500/20 hover:bg-red-500/20 transition-colors flex items-center gap-2 disabled:opacity-50"
              data-testid="btn-clear-cache-now"
            >
              {clearing ? (
                <><Loader2 className="w-3 h-3 animate-spin" /> Effacement en cours...</>
              ) : (
                <><Trash className="w-3 h-3" /> Effacer le cache et recharger</>
              )}
            </button>
            {clearedAt && (
              <div className="text-[10px] text-emerald-400 mt-2">Cache efface a {clearedAt}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Iter 215 — Section header for grouped API cards ─────────────
function ApiSectionHeader({ title, subtitle, color = "#f7c935" }) {
  return (
    <div className="flex items-center gap-3 pt-4 pb-1 border-b border-white/10" data-testid={`api-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="w-1 h-7 rounded-full" style={{ background: color }} />
      <div>
        <h3 className="text-sm font-bold text-white uppercase tracking-wider">{title}</h3>
        {subtitle && <p className="text-[10px] text-slate-500">{subtitle}</p>}
      </div>
    </div>
  );
}

